<template>
  <div class="qrcode-reader__container">
    <img
      class="qrcode-reader--close"
      src="@/assets/member/icon-close.svg"
      alt="Close icon"
      @click="$emit('close')"
    >
    <div class="qrcode-reader__label">找到條碼來掃描</div>

    <qrcode-stream
      v-if="!reload"
      class="qrcode-reader"
      camera="rear"
      @init="onInit"
      @decode="onDecode"
    >
      <div v-if="init" class="qrcode-reader__prompt">等待開啟相機...</div>
      <img class="qrcode-reader__border-tl" src="@/assets/member/icon-qrcode-border-tl.svg" alt="QRCode border">
      <img class="qrcode-reader__border-tr" src="@/assets/member/icon-qrcode-border-tr.svg" alt="QRCode border">
      <img class="qrcode-reader__border-bl" src="@/assets/member/icon-qrcode-border-bl.svg" alt="QRCode border">
      <img class="qrcode-reader__border-br" src="@/assets/member/icon-qrcode-border-br.svg" alt="QRCode border">
    </qrcode-stream>

    <div v-else v-loading="reload" class="qrcode-reader" element-loading-background="rgba(0,0,0,0.6)">
      <div v-if="init" class="qrcode-reader__prompt">等待開啟相機...</div>
      <img class="qrcode-reader__border-tl" src="@/assets/member/icon-qrcode-border-tl.svg" alt="QRCode border">
      <img class="qrcode-reader__border-tr" src="@/assets/member/icon-qrcode-border-tr.svg" alt="QRCode border">
      <img class="qrcode-reader__border-bl" src="@/assets/member/icon-qrcode-border-bl.svg" alt="QRCode border">
      <img class="qrcode-reader__border-br" src="@/assets/member/icon-qrcode-border-br.svg" alt="QRCode border">
    </div>

    <div class="p-[20px] ">
      <p class="text-white">本系統預設為「後鏡頭」，如與您的預設值不符，建議確認瀏覽器版本或更換裝置使用</p>
    </div>

    <SyncMemberDialog
      v-if="showSyncDialog && pendingUserData"
      :user-data="pendingUserData"
      :shop-name="shopName"
      @close="handleSyncDialogClose"
      @success="handleSyncSuccess"
    />
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue'
import { QrcodeStream } from 'vue-qrcode-reader'
import { useQrCodeParser } from '@/use/qrCodeParser'
import { CheckUserPhone } from '@/api/member'
import { useShop } from '@/use/shop'
import { useRouter } from 'vue-router/composables'
import SyncMemberDialog from '@/components/Dialog/SyncMemberDialog.vue'

export default defineComponent({
  name: 'QRCodeReaderModal',
  components: { QrcodeStream, SyncMemberDialog },
  props: {
    reload: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup (props, { emit }) {
    const init = ref(true)
    const { parseAndCallback } = useQrCodeParser()
    const { shopId, shopName } = useShop()
    const router = useRouter()
    const showSyncDialog = ref(false)
    const pendingUserData = ref(null)
    const pendingPhone = ref('')

    const onInit = async (promise) => {
      init.value = true
      try {
        await promise
      } catch (error) {
        console.log('qrcode error', error)
      } finally {
        init.value = false
      }
    }

    const checkPhoneAndShowDialog = async (phone) => {
      const [res, err] = await CheckUserPhone({ shopId: shopId.value, phone })

      if (err) {
        window.$message.error(err)
        emit('close')
        return
      }

      // 情境 1: 查無使用者
      if (!res.user) {
        window.$message.error('查無此會員')
        emit('close')
        return
      }

      // 情境 2: 已存在會員 - 直接導航
      if (res.member) {
        emit('close')
        router.push({
          name: 'MemberProfile',
          params: { id: res.member.id },
        })
        return
      }

      // 情境 3: 使用者存在但非本店會員 - 顯示同步確認對話框
      if (res.user && !res.member) {
        pendingUserData.value = res
        pendingPhone.value = phone
        showSyncDialog.value = true
      }
    }

    const onDecode = async (decodedString) => {
      // 檢查是否為手機號碼格式
      if (/^09\d{8}$/.test(decodedString)) {
        await checkPhoneAndShowDialog(decodedString)
      } else {
        // 非手機號碼，使用原有邏輯
        const success = await parseAndCallback(decodedString, (memberData) => {
          emit('close')
          router.push({
            name: 'MemberProfile',
            params: { id: memberData.id },
          })
        })

        if (!success) {
          // 解析失敗，保持掃描器開啟
          console.error('QR Code parsing failed')
        }
      }
    }

    const handleSyncDialogClose = () => {
      showSyncDialog.value = false
      pendingUserData.value = null
      pendingPhone.value = ''
      // 保持掃描器開啟，不呼叫 emit('close')
    }

    const handleSyncSuccess = () => {
      showSyncDialog.value = false
      pendingUserData.value = null
      pendingPhone.value = ''
      emit('close')
    }

    return {
      init,
      onInit,
      onDecode,
      showSyncDialog,
      pendingUserData,
      pendingPhone,
      handleSyncDialogClose,
      handleSyncSuccess,
      shopName,
    }
  },
})
</script>

<style lang="scss" scoped>
.qrcode-reader__container {
  z-index: 100;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, .6);
}

.qrcode-reader--close {
  position: absolute;
  top: 92.5px;
  right: 92.5px;
  width: 35px;
  height: 35px;
  cursor: pointer;
}

.qrcode-reader__label {
  color: white;
  font-size: 25px;
  line-height: 28px;
}

.qrcode-reader {
  position: relative;
  margin-top: 50px;
  width: 440px !important;
  height: 440px !important;
}

.qrcode-reader__prompt {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  color: white;
  background-color: rgba(0, 0, 0, .6);
}

.qrcode-reader__border-tl,
.qrcode-reader__border-tr,
.qrcode-reader__border-bl,
.qrcode-reader__border-br {
  position: absolute;
}

.qrcode-reader__border-tl {
  top: -6px;
  left: -6px;
}

.qrcode-reader__border-tr {
  top: -6px;
  right: -6px;
}

.qrcode-reader__border-bl {
  bottom: -6px;
  left: -6px;
}

.qrcode-reader__border-br {
  bottom: -6px;
  right: -6px;
}
</style>
